/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Modal, Button } from 'react-bootstrap';

const DateTimePicker = ({ 
  value = new Date(), 
  onChange, 
  label = 'Date & Time',
  className = '',
  disabled = false,
  use24Hour = false,
  show24HourToggle = false
}) => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(now.getDate());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedHour, setSelectedHour] = useState(now.getHours() % 12 || 12);
  const [selectedMinute, setSelectedMinute] = useState(Math.floor(now.getMinutes() / 5) * 5);
  const [isAM, setIsAM] = useState(now.getHours() < 12);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [showYearModal, setShowYearModal] = useState(false);
  const [showHourModal, setShowHourModal] = useState(false);
  const [showMinuteModal, setShowMinuteModal] = useState(false);
  const [showAMPMModal, setShowAMPMModal] = useState(false);
  const [isASAP, setIsASAP] = useState(false);
  const [internalUse24Hour, setInternalUse24Hour] = useState(use24Hour);
  
  // Add ref to track if we're updating from external value
  const isUpdatingFromValue = useRef(false);
  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initialize from value prop with error handling
  useEffect(() => {
    try {
      if (value && value instanceof Date && isMounted.current) {
        isUpdatingFromValue.current = true;
        setSelectedMonth(value.getMonth());
        setSelectedDate(value.getDate());
        setSelectedYear(value.getFullYear());
        setSelectedHour(value.getHours() % 12 || 12);
        setSelectedMinute(Math.floor(value.getMinutes() / 5) * 5);
        setIsAM(value.getHours() < 12);
        // Reset the flag after a short delay
        setTimeout(() => {
          if (isMounted.current) {
            isUpdatingFromValue.current = false;
          }
        }, 0);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error initializing from value:', error);
    }
  }, [value]);

  // Generate options for each dropdown
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const years = Array.from({ length: 3 }, (_, i) => now.getFullYear() + i); // Current year + next 2 years
  const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
  const hours24 = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  // Day initials for the grid header
  const dayInitials = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Calculate days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar grid data
  const generateCalendarGrid = (month, year) => {
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    const grid = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      grid.push({ day: '', isCurrentMonth: false, isSelected: false, isToday: false });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = day === selectedDate;
      const isToday = day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
      grid.push({ day, isCurrentMonth: true, isSelected, isToday });
    }
    
    return grid;
  };

  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const dates = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Check if a date is in the past (ignoring time) - only if not ASAP
  const isDateInPast = (year, month, date) => {
    if (isASAP) return false; // Skip validation if ASAP is enabled
    const selectedDate = new Date(year, month, date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return selectedDate < today;
  };

  // Update the date when month/year changes
  useEffect(() => {
    const maxDays = getDaysInMonth(selectedMonth, selectedYear);
    if (selectedDate > maxDays) {
      setSelectedDate(maxDays);
    }
  }, [selectedMonth, selectedYear, selectedDate]);

  // Handle ASAP toggle - adds 5 minutes to current time
  const handleASAPToggle = (e) => {
    const asapEnabled = e.target.checked;
    setIsASAP(asapEnabled);
    
    if (asapEnabled) {
      // Set to current date and time plus 5 minutes when ASAP is enabled
      const currentNow = new Date();
      currentNow.setMinutes(currentNow.getMinutes() + 5); // Add 5 minutes
      setSelectedMonth(currentNow.getMonth());
      setSelectedDate(currentNow.getDate());
      setSelectedYear(currentNow.getFullYear());
      setSelectedHour(currentNow.getHours() % 12 || 12);
      setSelectedMinute(Math.floor(currentNow.getMinutes() / 5) * 5);
      setIsAM(currentNow.getHours() < 12);
    }
    // Inform parent that ASAP changed
    if (onChange) {
      const currentNow = new Date();
      if (asapEnabled) currentNow.setMinutes(currentNow.getMinutes() + 5);
      const effectiveDate = asapEnabled
        ? currentNow
        : new Date(selectedYear, selectedMonth, (selectedDate || now.getDate()), (internalUse24Hour ? selectedHour : (isAM ? (selectedHour % 12) : ((selectedHour % 12) + 12))), selectedMinute);
      onChange(effectiveDate, asapEnabled);
    }
  };

  // Handle 24-hour toggle
  const handle24HourToggle = (e) => {
    const use24Hour = e.target.checked;
    setInternalUse24Hour(use24Hour);
    
    if (use24Hour) {
      // Convert 12-hour to 24-hour
      let hour24 = selectedHour;
      if (!isAM && selectedHour !== 12) hour24 += 12;
      if (isAM && selectedHour === 12) hour24 = 0;
      setSelectedHour(hour24);
    } else {
      // Convert 24-hour to 12-hour
      let hour12 = selectedHour;
      let am = true;
      if (selectedHour >= 12) {
        hour12 = selectedHour === 12 ? 12 : selectedHour - 12;
        am = false;
      }
      if (selectedHour === 0) {
        hour12 = 12;
        am = true;
      }
      setSelectedHour(hour12);
      setIsAM(am);
    }
  };

  // Handle month selection
  const handleMonthSelect = (monthIndex) => {
    try {
      if (isMounted.current) {
        setSelectedMonth(monthIndex);
        setShowMonthModal(false);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error in handleMonthSelect:', error);
    }
  };

  // Handle year selection
  const handleYearSelect = (year) => {
    try {
      if (isMounted.current) {
        setSelectedYear(year);
        setShowYearModal(false);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error in handleYearSelect:', error);
    }
  };

  // Handle hour selection
  const handleHourSelect = (hour) => {
    try {
      if (isMounted.current) {
        setSelectedHour(hour);
        setShowHourModal(false);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error in handleHourSelect:', error);
    }
  };

  // Handle minute selection
  const handleMinuteSelect = (minute) => {
    try {
      if (isMounted.current) {
        setSelectedMinute(minute);
        setShowMinuteModal(false);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error in handleMinuteSelect:', error);
    }
  };

  // Handle AM/PM selection
  const handleAMPMSelect = (isAMValue) => {
    try {
      if (isMounted.current) {
        setIsAM(isAMValue);
        setShowAMPMModal(false);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error in handleAMPMSelect:', error);
    }
  };

  // Handle date selection from grid
  const handleDateSelect = (day) => {
    try {
      if (day && !isDateInPast(selectedYear, selectedMonth, day) && isMounted.current) {
        setSelectedDate(day);
        setShowDateModal(false);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error in handleDateSelect:', error);
    }
  };

  // Update parent component whenever any value changes
  useEffect(() => {
    try {
      if (onChange && !isUpdatingFromValue.current && isMounted.current) {
        let finalHour = selectedHour;
        
        if (!internalUse24Hour) {
          // Convert to 24-hour for the final date
          if (!isAM && selectedHour !== 12) finalHour += 12;
          if (isAM && selectedHour === 12) finalHour = 0;
        }
        
        const finalDate = new Date(selectedYear, selectedMonth, selectedDate, finalHour, selectedMinute);
        onChange(finalDate, isASAP);
      }
    } catch (error) {
      console.warn('DateTimePicker: Error calling onChange:', error);
    }
  }, [selectedMonth, selectedDate, selectedYear, selectedHour, selectedMinute, isAM, internalUse24Hour, isASAP, onChange]);

  // Format the display date
  const formatDisplayDate = () => {
    const monthName = months[selectedMonth];
    const dateStr = selectedDate.toString().padStart(2, '0');
    return `${monthName} ${dateStr}, ${selectedYear}`;
  };

  // Format the display time
  const formatDisplayTime = () => {
    const hourStr = selectedHour.toString().padStart(2, '0');
    const minuteStr = selectedMinute.toString().padStart(2, '0');
    
    if (internalUse24Hour) {
      return `${hourStr}:${minuteStr}`;
    } else {
      const ampm = isAM ? 'AM' : 'PM';
      return `${hourStr}:${minuteStr} ${ampm}`;
    }
  };

  return (
    <div className={className}>
      {label && <Form.Label className="mb-3 fw-bold">{label}</Form.Label>}
      
      {/* ASAP Toggle */}
      <Row className="mb-3">
        <Col xs={12}>
          <Form.Check
            type="switch"
            id="asap-switch"
            label={
              <span>
                <span style={{ color: isASAP ? '#dc3545' : '#6c757d' }}>
                  🚨 ASAP (As Soon As Possible)
                </span>
                {isASAP && <span className="badge bg-danger ms-2">URGENT</span>}
              </span>
            }
            checked={isASAP}
            onChange={handleASAPToggle}
            disabled={disabled}
          />
        </Col>
      </Row>

      {/* 24-Hour Toggle (only shown if show24HourToggle is true) */}
      {show24HourToggle && (
        <Row className="mb-3">
          <Col xs={12}>
            <Form.Check
              type="switch"
              id="24hour-switch"
              label="Use 24-Hour Format"
              checked={internalUse24Hour}
              onChange={handle24HourToggle}
              disabled={disabled}
            />
          </Col>
        </Row>
      )}
      
      {/* Date Row - Month, Date, Year on one line */}
      <Row className="g-2 mb-3">
        <Col xs={4}>
          <Form.Label className="small text-muted mb-1">Month</Form.Label>
          <Form.Control
            type="text"
            value={months[selectedMonth]}
            onClick={() => !disabled && !isASAP && setShowMonthModal(true)}
            readOnly
            disabled={disabled || isASAP}
            size="sm"
            style={{ 
              cursor: (disabled || isASAP) ? 'not-allowed' : 'pointer',
              opacity: isASAP ? 0.6 : 1
            }}
          />
        </Col>
        <Col xs={4}>
          <Form.Label className="small text-muted mb-1">Date</Form.Label>
          <Form.Control
            type="text"
            value={selectedDate}
            onClick={() => !disabled && !isASAP && setShowDateModal(true)}
            readOnly
            disabled={disabled || isASAP}
            size="sm"
            style={{ 
              cursor: (disabled || isASAP) ? 'not-allowed' : 'pointer',
              opacity: isASAP ? 0.6 : 1
            }}
          />
        </Col>
        <Col xs={4}>
          <Form.Label className="small text-muted mb-1">Year</Form.Label>
          <Form.Control
            type="text"
            value={selectedYear}
            onClick={() => !disabled && !isASAP && setShowYearModal(true)}
            readOnly
            disabled={disabled || isASAP}
            size="sm"
            style={{ 
              cursor: (disabled || isASAP) ? 'not-allowed' : 'pointer',
              opacity: isASAP ? 0.6 : 1
            }}
          />
        </Col>
      </Row>

      {/* Time Row */}
      <Row className="g-3">
                  <Col xs={4}>
            <Form.Label className="small text-muted mb-1">Hour</Form.Label>
            <Form.Control
              type="text"
              value={selectedHour.toString().padStart(2, '0')}
              onClick={() => !disabled && !isASAP && setShowHourModal(true)}
              readOnly
              disabled={disabled || isASAP}
              size="sm"
              style={{ 
                cursor: (disabled || isASAP) ? 'not-allowed' : 'pointer',
                opacity: isASAP ? 0.6 : 1
              }}
            />
          </Col>
        <Col xs={4}>
          <Form.Label className="small text-muted mb-1">Minute</Form.Label>
          <Form.Control
            type="text"
            value={selectedMinute.toString().padStart(2, '0')}
            onClick={() => !disabled && !isASAP && setShowMinuteModal(true)}
            readOnly
            disabled={disabled || isASAP}
            size="sm"
            style={{ 
              cursor: (disabled || isASAP) ? 'not-allowed' : 'pointer',
              opacity: isASAP ? 0.6 : 1
            }}
          />
        </Col>
        {!internalUse24Hour && (
          <Col xs={4}>
            <Form.Label className="small text-muted mb-1">AM/PM</Form.Label>
            <Form.Control
              type="text"
              value={isAM ? 'AM' : 'PM'}
              onClick={() => !disabled && !isASAP && setShowAMPMModal(true)}
              readOnly
              disabled={disabled || isASAP}
              size="sm"
              style={{ 
                cursor: (disabled || isASAP) ? 'not-allowed' : 'pointer',
                opacity: isASAP ? 0.6 : 1
              }}
            />
          </Col>
        )}
        {isDateInPast(selectedYear, selectedMonth, selectedDate) && !isASAP && (
          <Col xs={12}>
            <div className="text-danger small mt-2">
              ⚠️ Selected date is in the past
            </div>
          </Col>
        )}
        {isASAP && (
          <Col xs={12}>
            <div className="text-danger small mt-2">
              🚨 ASAP Mode: Using current date and time
            </div>
          </Col>
        )}
      </Row>

      {/* Selected Date and Time Display */}
      <Row className="mt-3">
        <Col xs={12}>
          <div className={`p-3 rounded ${isASAP ? 'bg-danger text-white' : 'bg-light'}`}>
            <div className="fw-bold">
              {formatDisplayDate()} at {formatDisplayTime()}
              {isASAP && ' (ASAP)'}
            </div>
          </div>
        </Col>
      </Row>

      {/* Date Picker Modal */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">{months[selectedMonth]} {selectedYear}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="calendar-grid">
            {/* Day headers */}
            <div className="calendar-header">
              {dayInitials.map((initial, index) => (
                <div key={index} className="calendar-day-header">
                  {initial}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="calendar-body">
              {generateCalendarGrid(selectedMonth, selectedYear).map((day, index) => (
                <div
                  key={index}
                  className={`calendar-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${day.isSelected ? 'selected' : ''} ${day.isToday ? 'today' : ''}`}
                  onClick={() => handleDateSelect(day.day)}
                >
                  {day.day}
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowDateModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Month Picker Modal */}
      <Modal show={showMonthModal} onHide={() => setShowMonthModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">Select Month</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {months.map((month, index) => (
              <Button
                key={index}
                variant="outline-primary"
                size="sm"
                onClick={() => handleMonthSelect(index)}
                className={`${selectedMonth === index ? 'active' : ''}`}
              >
                {month}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowMonthModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Year Picker Modal */}
      <Modal show={showYearModal} onHide={() => setShowYearModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">Select Year</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {years.map((year) => (
              <Button
                key={year}
                variant="outline-primary"
                size="sm"
                onClick={() => handleYearSelect(year)}
                className={`${selectedYear === year ? 'active' : ''}`}
              >
                {year}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowYearModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hour Picker Modal */}
      <Modal show={showHourModal} onHide={() => setShowHourModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">Select Time Required</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          {/* CACHE BUST: COMPLETE LAYOUT RESTRUCTURE - VERSION 3.0 */}
          <div className="d-flex">
            <div className="flex-grow-1">
              {/* COMPLETE RESTRUCTURE: Two rows of 6 buttons each */}
              <div className="row mb-2 g-1">
                {(internalUse24Hour ? hours24 : hours12).slice(0, 6).map((hour) => (
                  <div key={hour} className="col-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleHourSelect(hour)}
                      className={`${selectedHour === hour ? 'active' : ''} w-100`}
                      style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                    >
                      {internalUse24Hour ? hour.toString().padStart(2, '0') : hour}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="row g-1">
                {(internalUse24Hour ? hours24 : hours12).slice(6, 12).map((hour) => (
                  <div key={hour} className="col-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleHourSelect(hour)}
                      className={`${selectedHour === hour ? 'active' : ''} w-100`}
                      style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                    >
                      {internalUse24Hour ? hour.toString().padStart(2, '0') : hour}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            {!internalUse24Hour && (
              <div className="ms-2 d-flex flex-column gap-1">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleAMPMSelect(true)}
                  className={`${isAM ? 'active' : ''}`}
                  style={{ minWidth: '45px', fontSize: '0.8rem' }}
                >
                  am
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleAMPMSelect(false)}
                  className={`${!isAM ? 'active' : ''}`}
                  style={{ minWidth: '45px', fontSize: '0.8rem' }}
                >
                  pm
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowHourModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Minute Picker Modal */}
      <Modal show={showMinuteModal} onHide={() => setShowMinuteModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">Select Minute</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          {/* CACHE BUST: COMPLETE LAYOUT RESTRUCTURE - VERSION 3.0 */}
          {/* COMPLETE RESTRUCTURE: Two rows of 6 buttons each */}
                     <div className="row mb-2 g-1">
             {minutes.slice(0, 6).map((minute) => (
               <div key={minute} className="col-2">
                 <Button
                   variant={minute === 0 || minute === 15 ? "outline-success" : "outline-primary"}
                   size="sm"
                   onClick={() => handleMinuteSelect(minute)}
                   className={`${selectedMinute === minute ? 'active' : ''} w-100`}
                   style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                 >
                   {minute.toString().padStart(2, '0')}
                 </Button>
               </div>
             ))}
           </div>
           <div className="row g-1">
             {minutes.slice(6, 12).map((minute) => (
               <div key={minute} className="col-2">
                 <Button
                   variant={minute === 30 || minute === 45 ? "outline-success" : "outline-primary"}
                   size="sm"
                   onClick={() => handleMinuteSelect(minute)}
                   className={`${selectedMinute === minute ? 'active' : ''} w-100`}
                   style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                 >
                   {minute.toString().padStart(2, '0')}
                 </Button>
               </div>
             ))}
           </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowMinuteModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* AM/PM Picker Modal */}
      <Modal show={showAMPMModal} onHide={() => setShowAMPMModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select am/pm</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleAMPMSelect(true)}
              className={`${isAM ? 'active' : ''}`}
            >
              am
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleAMPMSelect(false)}
              className={`${!isAM ? 'active' : ''}`}
            >
              pm
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowAMPMModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .calendar-grid {
          width: 100%;
        }
        
        .calendar-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          margin-bottom: 8px;
        }
        
        .calendar-day-header {
          text-align: center;
          font-weight: bold;
          font-size: 0.7rem;
          color: #6c757d;
          padding: 2px;
        }
        
        .calendar-body {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }
        
        .calendar-day {
          text-align: center;
          padding: 4px 2px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 0.75rem;
          transition: background-color 0.2s;
          min-height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .calendar-day:hover {
          background-color: #e9ecef;
        }
        
        .calendar-day.current-month {
          color: #212529;
        }
        
        .calendar-day.other-month {
          color: #adb5bd;
        }
        
        .calendar-day.selected {
          background-color: #0d6efd;
          color: white;
        }
        
        .calendar-day.today {
          background-color: #ffc107;
          color: #212529;
          font-weight: bold;
        }
        
        .calendar-day.selected.today {
          background-color: #0d6efd;
          color: white;
        }
        
        /* Bootstrap Row/Col layout used for Hour and Minute pickers */
      `}</style>
    </div>
  );
};

export default DateTimePicker; 